import type { CaptainSave } from '@realseas/shared';
import { getPort, getOrigin, destinationsFrom } from '@realseas/shared';
import type { Connection } from '../Connection.js';
import { setHTML, esc } from '../dom.js';

function ownedOf(captain: CaptainSave, itemId: string): number {
  return captain.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days} days, ${hours} hours, ${minutes} minutes`;
  if (hours > 0) return `${hours} hours, ${minutes} minutes`;
  return `${minutes} minutes`;
}

/** Client-only: whether the captain has clicked "stay" and is looking at the card. */
let showingCard = false;
let cardForToken: string | null = null;

export function resetArrivalState(): void {
  showingCard = false;
  cardForToken = null;
}

/**
 * The port after a crossing. First the harbormaster's question — stay, or
 * shove off? — then either the sealed voyage card (with the way back to the
 * main menu) or the next leg.
 */
export function renderArrivalScreen(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
  serverTime: number,
): void {
  if (cardForToken !== captain.token) resetArrivalState();
  cardForToken = captain.token;

  const here = captain.destinationId ? getPort(captain.destinationId) : undefined;
  const hereName = here?.name ?? 'port';
  const isTortuga = here?.region === 'caribbean';

  if (showingCard) {
    renderCard(container, conn, captain, serverTime, hereName, isTortuga);
    return;
  }

  const nextPorts = destinationsFrom(here?.id ?? null);
  const nextHint = isTortuga
    ? 'Shove off, and you may set a course for any port in Europe.'
    : 'Shove off, and it is back across the Atlantic to Tortuga.';

  setHTML(container, `
    <div class="tortuga-screen">
      <h1 class="title">${esc(hereName)}</h1>
      <p class="subtitle">Land ho. The anchor is down.</p>

      <div class="arrival-prompt">
        <div class="seal" aria-hidden="true">⚓</div>
        <p class="arrival-greeting">
          “Arrr — thanks for the cargo, Captain ${esc(captain.username)}. Well done making the voyage.
          ${isTortuga
            ? 'The town stinks of rum, fish, and possibility.'
            : `The bells of ${esc(hereName)} ring you in like a prodigal.`}
          Now — are you <strong>staying</strong>, or <strong>shoving off</strong>?”
        </p>
        <div class="arrival-choices">
          <button class="secondary" id="arrival-stay-btn">Stay ashore</button>
          <button class="primary" id="arrival-continue-btn">Shove off</button>
        </div>
        <p class="arrival-hint">
          Stay, and your ledger is sealed — the voyage is done and the name goes back on the wall.
          ${esc(nextHint)}
          ${nextPorts.length > 1 ? ` (${nextPorts.map(p => p.name).join(', ')}.)` : ''}
          Either way, the dockmaster buys what you care to sell.
        </p>
      </div>
    </div>
  `);

  container.querySelector<HTMLButtonElement>('#arrival-stay-btn')!.onclick = () => {
    showingCard = true;
    renderCard(container, conn, captain, serverTime, hereName, isTortuga);
  };
  container.querySelector<HTMLButtonElement>('#arrival-continue-btn')!.onclick = () => {
    conn.send({ type: 'continue_voyage' });
  };
}

function renderCard(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
  serverTime: number,
  hereName: string,
  isTortuga: boolean,
): void {
  const port = captain.portId ? getPort(captain.portId) : null;
  const origin = captain.originId ? getOrigin(captain.originId) : null;
  const elapsed = captain.embarkedAt ? serverTime - captain.embarkedAt : 0;
  const sailorsLeft = ownedOf(captain, 'sailor');
  const fishCaught = ownedOf(captain, 'fish_caught');
  const whaleMeat = ownedOf(captain, 'whale_meat');
  const legs = captain.voyagesCompleted;

  setHTML(container, `
    <div class="tortuga-screen">
      <h1 class="title">${esc(hereName)}</h1>
      <p class="subtitle">The voyage is complete.</p>

      <div class="victory-card">
        <div class="seal" aria-hidden="true">♛</div>
        <h2>Captain ${esc(captain.username)}</h2>
        <p class="epithet">${esc(origin?.name ?? '')}, late of ${esc(port?.name ?? 'a forgotten port')}</p>
        <div class="divider"></div>
        <p class="victory-flavor">
          The anchor splashes into the ${isTortuga ? 'turquoise' : 'grey harbor'} water. After ${esc(formatElapsed(elapsed))} at sea,
          your ship is hove to in the road of ${esc(hereName)}.
          ${isTortuga ? 'The town stinks of rum, fish, and possibility.' : 'You are home, and the sea is behind you.'}
        </p>

        <div class="victory-stats">
          <div class="stat"><span class="key">This crossing</span><span class="val">${esc(formatElapsed(elapsed))}</span></div>
          <div class="stat"><span class="key">Crossings made</span><span class="val">${legs}</span></div>
          <div class="stat"><span class="key">Crew surviving</span><span class="val">${sailorsLeft}</span></div>
          <div class="stat"><span class="key">Hull integrity</span><span class="val">${captain.ship?.hullIntegrity?.toFixed(0) ?? '?'}%</span></div>
          <div class="stat"><span class="key">Reales in the chest</span><span class="val">${captain.reales.toLocaleString()}</span></div>
          <div class="stat"><span class="key">Enemy ships sunk</span><span class="val">${captain.shipsSunk}</span></div>
          <div class="stat"><span class="key">Whales taken</span><span class="val">${captain.whalesKilled}</span></div>
          <div class="stat"><span class="key">Fish in the hold</span><span class="val">${fishCaught}</span></div>
          <div class="stat"><span class="key">Whale meat</span><span class="val">${whaleMeat}</span></div>
        </div>

        <p style="font-style: italic; color: var(--ink-faint); margin-top: 18px;">
          You step ashore for good. Somewhere a clerk strikes your name from the register of ships at sea.
        </p>

        <div class="arrival-choices" style="margin-top: 22px;">
          <button class="primary" id="arrival-menu-btn">Return to Main Menu</button>
          <button class="secondary" id="arrival-back-btn">On second thought — shove off</button>
        </div>
      </div>
    </div>
  `);

  container.querySelector<HTMLButtonElement>('#arrival-menu-btn')!.onclick = () => {
    const ok = confirm(
      `Settle ashore in ${hereName}?\n\nThis erases Captain ${captain.username}'s save — ship, crew, chest, and chart. The name will be free for a new voyage.`,
    );
    if (ok) conn.send({ type: 'retire' });
  };
  container.querySelector<HTMLButtonElement>('#arrival-back-btn')!.onclick = () => {
    showingCard = false;
    renderArrivalScreen(container, conn, captain, serverTime);
  };
}
